import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useParams, useHistory } from 'react-router-dom';
import cn from 'classnames';
import './ReferNewCreator.scss';

import ScrollToTop from '../../Components/General/ScrollToTop';
import { addEvent } from '../../APIClient/events';
import { sendReferralEmails as sendReferralEmailsAPI } from '../../APIClient/referrals';

import { getUsername, getUserId } from '../../Helpers/user_helpers';
import { isAdminControlMode } from '../../Helpers/ui_helpers';
import { copyToClipboard } from '../../Helpers/helpers';
import { isValidEmail } from '../../Helpers/formatting';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes, faCopy } from '@fortawesome/pro-solid-svg-icons';

const ReferNewCreator = props => {
  const { user, ui } = props;
  const history = useHistory();
  const params = useParams();
  const username = getUsername(user);
  const usernameFromParams = params.username;

  const [emails, setEmails] = useState([{ email: '', isValid: true }]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const addAnotherEmail = () => setEmails([...emails, { email: '', isValid: true }]);

  useEffect(() => {
    if (!getUserId(user) || (username !== usernameFromParams && !isAdminControlMode(ui))) {
      window.ALERT.error('You do not have access to this page.');
      history.push('/home');
    } else {
      addEvent(`Referral - Referral Page Sign Up View`, { user: username });
    }
  }, [user.username]);

  useEffect(() => {
    for (let i = 0; i < emails.length; i++) {
      const { email, isValid } = emails[i];
      if (isValid) continue;

      if (email && isValidEmail(email)) {
        const newEmails = [...emails];
        newEmails[i].isValid = true;
        setEmails(newEmails);
      }
    }
  }, [emails]);

  const submitEmails = async () => {
    if (isSubmitting) return window.ALERT.warn('Please wait for your previous request to finish.');

    let invalidEmailCount = 0;
    const newEmails = [...emails];
    for (let i = 0; i < newEmails.length; i++) {
      const email = newEmails[i].email;
      if (email && !isValidEmail(email)) {
        newEmails[i].isValid = false;
        invalidEmailCount++;
      } else {
        newEmails[i].isValid = true;
      }
    }

    if (invalidEmailCount > 0) {
      setEmails(newEmails);
      window.ALERT.error('Please enter valid email addresses.');
      return;
    }

    try {
      setIsSubmitting(true);

      const loneEmails = newEmails.map(emailObj => emailObj.email);
      await sendReferralEmailsAPI(loneEmails, getUserId(user));

      window.ALERT.success(`${loneEmails.length > 1 ? 'Emails' : 'Email'} sent successfully!`);
      setEmails([{ email: '', isValid: true }]);
    } catch (error) {
      window.ALERT.error('Error sending emails. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className='refer-new-creator-outer'>
      <ScrollToTop />
      <div className='refer-new-creator-inner'>
        <div className='refer-new-creator-growing-outer'>
          <div className='refer-new-creator-growing-inner'>
            <div className='refer-new-creator-growing-title'>
              <h1>Growing  the ShopMy Community</h1>
              {/* TODO: Creator Application uncomment this when phase 2 goes live */}
              {/* <h2>Earn up to $1500 for each creator you refer + up to 20 bonus points towards your creator tier</h2> */}
            </div>

            <div className='refer-new-creator-growing-referral-container'>
              <div className='refer-new-creator-growing-referral'>
                <div className='refer-new-creator-growing-referral-option'>Option 1</div>
                <div className='refer-new-creator-growing-referral-title-container'>
                  <div className='refer-new-creator-growing-referral-title'>Share your Referral Link</div>
                  <div className='refer-new-creator-growing-referral-subtitle'>
                    Share your link with creators via group chats, YouTube descriptions, Instagram stories, or TikTok bio. Explain how ShopMy helps
                    creators monetize their influence through affiliate links and direct brand partnerships.
                  </div>
                </div>

                <div className='refer-new-creator-input-container'>
                  <div className='refer-new-creator-input'>
                    <input value={`https://shopmy.us/join/${usernameFromParams}`} readOnly />

                    <span
                      className='refer-new-creator-input-icon-container'
                      onClick={e => {
                        e.stopPropagation();
                        copyToClipboard(`https://shopmy.us/join/${usernameFromParams}`, true);
                      }}
                    >
                      <FontAwesomeIcon icon={faCopy} />
                    </span>
                  </div>
                  <button className='refer-new-creator-button' onClick={() => copyToClipboard(`https://shopmy.us/join/${usernameFromParams}`, true)}>
                    Copy Link
                  </button>
                </div>
              </div>

              <div className='refer-new-creator-growing-referral'>
                <div className='refer-new-creator-growing-referral-option'>Option 2</div>

                <div className='refer-new-creator-growing-referral-title-container'>
                  <div className='refer-new-creator-growing-referral-title'>Send Invitation Email</div>
                  <div className='refer-new-creator-growing-referral-subtitle'>
                    Send an invite via email to creators who you know and want to be on ShopMy (check their Instagram page for their email, if you
                    don’t know it).
                  </div>
                </div>

                <div className='refer-new-creator-input-container'>
                  {emails.map(({ email, isValid }, index) => (
                    <div key={index} className='refer-new-creator-input'>
                      <input
                        key={index}
                        value={email}
                        placeholder='Email'
                        className={cn('refer-new-creator-input-field', { invalid: !isValid })}
                        onChange={e => {
                          const newEmails = [...emails];
                          newEmails[index].email = e.target.value;
                          setEmails(newEmails);
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            submitEmails();
                          } else if (e.key === 'Tab') {
                            e.preventDefault();
                            addAnotherEmail();
                          }
                        }}
                      />

                      {index > 0 && (
                        <span
                          className='refer-new-creator-input-icon-container'
                          onClick={e => {
                            e.stopPropagation();
                            const newEmails = [...emails];
                            newEmails.splice(index, 1);
                            setEmails(newEmails);
                          }}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </span>
                      )}
                    </div>
                  ))}
                  <div className='refer-new-creator-growing-referral-add-email' onClick={addAnotherEmail}>
                    <span>Email</span>
                    <span className='refer-new-creator-input-icon-container'>
                      <FontAwesomeIcon icon={faPlus} />
                    </span>
                  </div>
                  <button className={cn('refer-new-creator-button', { isSubmitting })} onClick={submitEmails}>
                    Send Emails
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* TODO: Creator Application uncomment this when phase 2 goes live */}
        {/* <div className='refer-new-creator-referral-system-outer'> */}
        {/*   <div className='refer-new-creator-referral-system-inner'> */}
        {/*     <div className='refer-new-creator-referral-system-header'> */}
        {/*       <div className='refer-new-creator-referral-system-subheader'>Creator Referrals</div> */}
        {/*       <h3>Referral Bonus System</h3> */}
        {/*       <div className='refer-new-creator-referral-system-body'> */}
        {/*         As the ShopMy community grows, your opportunities grow. Referring creators will earn you bonus points to get you to the next tier - */}
        {/*         please note, for you to maintain your bonus points, your referred creators must maintain their tier. See below for how bonus points */}
        {/*         are calculated. */}
        {/*       </div> */}
        {/*     </div> */}
        {/**/}
        {/*     <div className='refer-new-creator-referral-system-table'> */}
        {/*       <div className='refer-new-creator-referral-system-table-header'> */}
        {/*         <div className='refer-new-creator-referral-system-table-row'> */}
        {/*           <div className='refer-new-creator-referral-system-table-cell left'>Referral Tier</div> */}
        {/*           <div className='refer-new-creator-referral-system-table-cell center'>Bonus</div> */}
        {/*           <div className='refer-new-creator-referral-system-table-cell right'>Bonus Points</div> */}
        {/*         </div> */}
        {/*       </div> */}
        {/**/}
        {/*       <div className='refer-new-creator-referral-system-table-body'> */}
        {/*         <div className='refer-new-creator-referral-system-table-row'> */}
        {/*           <div className='refer-new-creator-referral-system-table-cell left'>Ambassador</div> */}
        {/*           <div className='refer-new-creator-referral-system-table-cell center'>$25</div> */}
        {/*           <div className='refer-new-creator-referral-system-table-cell right'>1 point</div> */}
        {/*         </div> */}
        {/**/}
        {/*         <div className='refer-new-creator-referral-system-table-row'> */}
        {/*           <div className='refer-new-creator-referral-system-table-cell left'>Trendsetter</div> */}
        {/*           <div className='refer-new-creator-referral-system-table-cell center'>$100</div> */}
        {/*           <div className='refer-new-creator-referral-system-table-cell right'>3 points</div> */}
        {/*         </div> */}
        {/**/}
        {/*         <div className='refer-new-creator-referral-system-table-row'> */}
        {/*           <div className='refer-new-creator-referral-system-table-cell left'>Icon</div> */}
        {/*           <div className='refer-new-creator-referral-system-table-cell center'>$1375</div> */}
        {/*           <div className='refer-new-creator-referral-system-table-cell right'>6 points</div> */}
        {/*         </div> */}
        {/*       </div> */}
        {/*     </div> */}
        {/**/}
        {/*     <div className='refer-new-creator-referral-system-buttons'> */}
        {/*       <a href={'https://shopmy.us/settings?tab=Creator+Tier'} className='refer-new-creator-button'> */}
        {/*         Track My Referrals */}
        {/*       </a> */}
        {/*       <p className='refer-new-creator-referral-system-disclaimer'> */}
        {/*         *Your bonus pts will be capped at 20. Your referred creators must maintain their tier to maintain your bonus pts */}
        {/*       </p> */}
        {/*     </div> */}
        {/*   </div> */}
        {/* </div> */}

        <div className='refer-new-creator-faqs-outer'>
          <div className='refer-new-creator-faqs-inner'>
            <div className='refer-new-creator-faqs-header'>
              <div className='refer-new-creator-faqs-subheader'>Frequently Asked Questions</div>
              <h3>Here to make your referral process easy</h3>
            </div>

            <div className='refer-new-creator-faqs-card-container'>
              <div className='refer-new-creator-faqs-card'>
                <div className='refer-new-creator-faqs-card-header'>How should I use my referral link?</div>
                <div className='refer-new-creator-faqs-card-body'>
                  Share your link with creators via Text/DM, in group chats. Explain how ShopMy helps creators monetize their influence through
                  affiliate links and direct brand partnerships.
                </div>
              </div>

              <div className='refer-new-creator-faqs-card'>
                <div className='refer-new-creator-faqs-card-header'>Who is a good fit for ShopMy?</div>
                <div className='refer-new-creator-faqs-card-body'>
                  A creator who has an engaged and loyal audience on at least one social platform, and who is regularly posting shoppable content
                  (daily/weekly) in areas like Beauty, Family, Fashion, Fitness, Food/Drink, Home, Lifestyle, Menswear, Sports, or Travel.
                </div>
              </div>

              <div className='refer-new-creator-faqs-card'>
                <div className='refer-new-creator-faqs-card-header'>What are the benefits of referring?</div>
                <div className='refer-new-creator-faqs-card-body'>
                  Referring creators will earn you bonus points to get you to the next tier - please note, for you to maintain your bonus points, your
                  referred creators must maintain their tier.
                  {/* TODO: Creator Application uncomment this when phase 2 goes live */}
                  {/*Also, you can earn up to $1500 for each creator you refer. You will earn $25 when your
                  referred creator reaches Ambassador, a further $100 when they reach Trendsetter and $1375 when they reach Icon. Your referred
                  creators will also earn the normal ShopMy bonuses as they upgrade their tier.
                  */}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='refer-new-creator-learn-more-outer'>
          <div className='refer-new-creator-learn-more-inner'>
            <h3 className='refer-new-creator-learn-more-header'>Want to learn more?</h3>
            <a href='https://guide.shopmy.us/' className='refer-new-creator-button'>
              Explore Our Creator Guide
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

ReferNewCreator.propTypes = {
  user: PropTypes.object.isRequired
};

const mapStateToProps = state => {
  const { user, ui } = state;
  return { user, ui };
};

export default connect(mapStateToProps, {})(ReferNewCreator);
